import React from 'react';
import { useTranslation } from 'react-i18next';

interface NoResultsProps {
  /** `ico--*` modifier class for the glyph (defaults to the search icon). */
  icon?: string;
  /** i18n key for the title (defaults to the generic "no results found"). */
  titleKey?: string;
  /** i18n key for the description line. Pass an empty string to omit it. */
  descKey?: string;
  compact?: boolean;
  className?: string;
}

/**
 * Shared empty state for tables — an icon in a soft circle with decorative sparkles,
 * matching the notification-center empty state. Defaults to the "no results found" search
 * variant; pass `icon` / `titleKey` / `descKey` for other empties (e.g. an empty group with
 * `ico--folder`). Styles live in the global `assets/scss/no-results.scss`.
 */
const NoResults: React.FC<NoResultsProps> = ({
  icon = 'ico--search',
  titleKey = 'noResultsFound',
  descKey = 'noResultsFoundDesc',
  compact = false,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div className={`no-results ${compact ? 'no-results--compact' : ''} ${className}`.trim()}>
      <div className="no-results__icon">
        <span className="no-results__spark no-results__spark--1" />
        <span className="no-results__spark no-results__spark--2" />
        <span className="no-results__spark no-results__spark--3" />
        <span className="no-results__spark no-results__spark--4" />
        <div className="no-results__circle">
          <span className={`ico ${icon} no-results__glyph`} />
        </div>
      </div>
      <div className="no-results__title">{t(titleKey)}</div>
      {descKey && <div className="no-results__desc">{t(descKey)}</div>}
    </div>
  );
};

export default NoResults;
